import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash, UsersThree } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Phase 207i — Standalone "Διαχειριστική Επιτροπή" editor for a single building.
 *
 * Lives in BuildingDetails (Overview header) so the managers list is decoupled
 * from the basic building info form. The same `building.managers[]` array
 * powers the meeting-approval workflow.
 */
export default function BuildingManagersDialog({ open, onOpenChange, buildingId, onSaved }) {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !buildingId) return;
    setLoading(true);
    axios
      .get(`${API}/buildings/${buildingId}`)
      .then((r) => setManagers(r.data?.managers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, buildingId]);

  const updateAt = (idx, patch) => {
    const next = [...managers];
    next[idx] = { ...next[idx], ...patch };
    setManagers(next);
  };

  const removeAt = (idx) => {
    setManagers(managers.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    setManagers([...managers, { name: '', phone: '', email: '' }]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = managers
        .map((m) => ({
          name: (m.name || '').trim(),
          phone: (m.phone || '').trim(),
          email: (m.email || '').trim(),
        }))
        .filter((m) => m.name);
      await axios.patch(`${API}/buildings/${buildingId}`, { managers: cleaned });
      toast.success('Αποθηκεύτηκε');
      onSaved && onSaved(cleaned);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-none" data-testid="managers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UsersThree size={18} weight="fill" className="text-zinc-600" />
            Διαχειριστική Επιτροπή
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Οι διαχειριστές χρησιμοποιούνται για τη ροή έγκρισης συνελεύσεων (emails + approvals).
          </p>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-zinc-400 text-sm">Φόρτωση…</div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                Μέλη ({managers.length})
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-none h-7 text-xs"
                onClick={addRow}
                data-testid="add-manager-btn"
              >
                <Plus size={14} className="mr-1" />
                Προσθήκη
              </Button>
            </div>

            {managers.length === 0 && (
              <p className="text-center text-xs text-zinc-400 py-4 italic">Δεν υπάρχουν διαχειριστές.</p>
            )}

            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {managers.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`manager-row-${i}`}>
                  <Input
                    placeholder="Όνομα"
                    value={m.name || ''}
                    onChange={(e) => updateAt(i, { name: e.target.value })}
                    className="col-span-4 rounded-none h-8 text-sm"
                    data-testid={`manager-name-${i}`}
                  />
                  <Input
                    placeholder="Τηλέφωνο"
                    value={m.phone || ''}
                    onChange={(e) => updateAt(i, { phone: e.target.value })}
                    className="col-span-3 rounded-none h-8 text-sm font-mono"
                    data-testid={`manager-phone-${i}`}
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={m.email || ''}
                    onChange={(e) => updateAt(i, { email: e.target.value })}
                    className="col-span-4 rounded-none h-8 text-sm"
                    data-testid={`manager-email-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    className="col-span-1 p-1 text-red-400 hover:text-red-600 justify-self-center"
                    data-testid={`remove-manager-${i}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">
            Άκυρο
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-zinc-950 rounded-none" data-testid="save-managers-btn">
            {saving ? 'Αποθήκευση…' : 'Αποθήκευση'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
